import { defineComponent, h, ref, computed, onMounted, onUnmounted } from 'vue';
// @ts-ignore
import { BTable } from 'buefy/dist/esm/table';
import { column, loader } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { Button } from '@/ui/components/Button';
import { useTask } from '@/ui/concerns';
import { getApiKey, getSecretApiKey } from '@/services/api/resources/organization/actions';
import { user } from '@/ui/auth/auth';
import { tooltip } from '@/ui/components/Tooltip';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import copyToClipboard from 'copy-to-clipboard';
import { toasted } from '@/ui/helpers';

interface TableColumn {
  title: string;
  key: string;
  isSecret: boolean;
  hasFetched: boolean;
}

export default defineComponent({
  setup() {
    const hiddenKey = '**************';

    const keys = ref([
      { title: t('api-keys.api-key'), key: '...', isSecret: false, hasFetched: false },
      { title: t('api-keys.api-secret'), key: hiddenKey, isSecret: true, hasFetched: false },
    ]);

    const onCopyKeyClick = async (value: TableColumn) => {
      try {
        await copyToClipboard(value.key);
        toasted.success(t('api-keys.success-copy-to-clipboard'));
      } catch (e) {
        toasted.error(t('api-keys.error-copy-to-clipboard'));
      }
    };

    const setPublicKey = (key: string) => {
      const index = keys.value.findIndex(k => !k.isSecret);

      keys.value[index].key = key;
    };

    const getApiKeyTask = useTask(
      () => getApiKey(user.value.organization.id),
      key => {
        setPublicKey(key.data.data.apiKey);
      },
    );

    const setPrivateKey = (key: string, hasFetched: boolean) => {
      const index = keys.value.findIndex(k => k.isSecret);

      keys.value[index].key = key;
      keys.value[index].hasFetched = hasFetched;
    };

    const getSecretApiKeyTask = useTask(
      () => getSecretApiKey(user.value.organization.id),
      key => {
        setPrivateKey(key.data.data.apiSecret, true);
      },
    );

    const secretKey = computed(() => keys.value.find(key => key.isSecret)!);

    const onFetchSecretKeyClick = () => {
      if (secretKey.value.hasFetched) {
        setPrivateKey(hiddenKey, false);

        return;
      }

      getSecretApiKeyTask.run();
    };

    const onApiKeyRegenerated = () => {
      setPrivateKey(hiddenKey, false);
      getApiKeyTask.run();
    };

    onMounted(() => {
      EventBus.$on(Events.APIKeysRegenerated, onApiKeyRegenerated);
    });

    onUnmounted(() => {
      EventBus.$off(Events.APIKeysRegenerated, getApiKeyTask.run);
    });

    return function render() {
      return loader('div', getApiKeyTask, [
        h(BTable, {
          props: {
            data: keys.value,
          },
        }, [
          column({
            width: '20%',
            field: 'title',
            label: t('api-keys.title'),
          }, {
            default: ({ row: apiKey }: { row: TableColumn }) => apiKey.title,
          }),
          column({
            width: '70%',
            field: 'key',
            label: t('api-keys.key'),
          }, {
            default: ({ row: apiKey }: { row: TableColumn }) => apiKey.key,
          }),
          column({
            headerClass: 'api-keys-index__header-actions',
            field: 'actions',
            label: t('api-keys.actions'),
          }, {
            default: ({ row: apiKey }: { row: TableColumn }) => h('div', { class: 'api-keys-index__actions' }, [
              apiKey.isSecret
              && tooltip({
                label: t(secretKey.value.hasFetched ? 'api-keys.hide-key' : 'api-keys.show-key'),
                position: 'is-left',
              }, [
                h(Button, {
                  class: 'mr-2',
                  props: { type: 'is-light', loading: getSecretApiKeyTask.state.value.pending },
                  attrs: {
                    iconRight: secretKey.value.hasFetched ? 'eye-slash' : 'eye',
                    iconPack: 'far',
                  },
                  on: { click: onFetchSecretKeyClick },
                }),
              ]),
              tooltip({ label: t('api-keys.copy-key'), position: 'is-left' }, [
                h(Button, {
                  class: 'mr-2 has-text-weight-semibold',
                  props: { type: 'is-light' },
                  attrs: {
                    iconRight: 'clone',
                    iconPack: 'far',
                  },
                  on: { click: () => onCopyKeyClick(apiKey) },
                }),
              ]),
            ]),
          }),
        ]),
      ]);
    };
  },
});
